
import { createNamespacedHelpers } from '@/common/vuex-composition-hooks';
import { defineComponent, onBeforeMount, reactive, toRefs } from 'vue';
import { IAuthActions, IAuthGetters, IAuthState } from '../login/types';
import BaseIcon from '@/components/BaseIcon.vue';
import CheckboxComponent from '../settingPage/components/CheckboxComponent.vue';
const Auth = createNamespacedHelpers<IAuthState, IAuthGetters, IAuthActions>(
  'auth'
);

import { useRoute } from 'vue-router';
import { firstUpperString } from '@/common/helpers/FirstUpperString';
import { mapActions } from 'vuex';

import { BarChart, LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer, SVGRenderer } from 'echarts/renderers';

use([
  BarChart,
  LineChart,
  CanvasRenderer,
  SVGRenderer,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
]);

interface State {
  brandFilters: IFilter[];
  salesChannelFilters: IFilter[];
  billingUnitsEmp: IFilter[];
  billingUnitsFil: IFilter[];
  segmentFilters: IFilter[];
}

interface IFilter {
  id?: number;
  index?: number;
  name?: string;
  fullname: string;
  selected?: boolean;
  code?: any;
  route?: string;
  module?: string;
}

export default defineComponent({
  components: {
    BaseIcon,
    CheckboxComponent,
  },

  data() {
    return {
      filters: [
        {
          data: 'brandFilters',
          name: 'Marcas',
          param: 'brandCodes',
          type: 'string',
        },
        {
          data: 'billingUnitsEmp',
          name: 'Empresas',
          param: 'billingUnitsEmp',
          type: 'number',
        },
        {
          data: 'billingUnitsFil',
          name: 'Filiais',
          param: 'billingUnitsFil',
          type: 'number',
        },
        {
          data: 'salesChannelFilters',
          name: 'Canais',
          param: 'channelCode',
          type: 'string',
        },
        {
          data: 'segmentFilters',
          name: 'Segmentos',
          param: 'segments',
          type: 'string',
        },
      ],
      searchParams: {},
    };
  },

  computed: {
    currentFormattedDate() {
      const date = new Date().toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });

      return date;
    },
  },

  methods: {
    ...mapActions('dashboardAllocatedAccomplished', {
      fetchAllocatedAccomplished: 'fetchAllocatedAccomplished',
      setSearchParams: 'setSearchParams',
    }),

    getFilter(filter: string) {
      let type = 'number';
      this.filters.forEach((f) => {
        if (f.data == filter) type = f.type;
      });

      if (type == 'number') {
        return this[filter].sort(
          (a, b) => Number(a.fullname) - Number(b.fullname)
        );
      } else if (type == 'string') {
        return this[filter].sort((a, b) =>
          a.fullname.localeCompare(b.fullname)
        );
      }
      return this[filter];
    },

    changeSearchParams(filter: string, option: string) {
      let i = '';
      this.searchParams[filter].forEach((f, index) => {
        if (f.id == option) i = index;
      });

      const checkboxvalue = this.searchParams[filter][i].selected;
      this.searchParams[filter][i].selected = !checkboxvalue;
    },

    setInitialSearchParams() {
      this.filters.forEach((filter) => {
        this.searchParams[filter.data] = this[filter.data];
      });
    },

    clearFilters() {
      this.filters.forEach((filter) => {
        this.searchParams[filter.data].forEach((el) => {
          el.selected = false;
        });
      });
      this.applySearchParams();
      this.fetchAllocatedAccomplished();
    },

    applySearchParams() {
      let filters = {};
      this.filters.forEach((filter) => {
        filters[filter.param] = [];
        Object.keys(this.searchParams[filter.data]).forEach((param) => {
          if (this.searchParams[filter.data][param].selected) {
            filters[filter.param].push(
              this.searchParams[filter.data][param].code
            );
          }
        });
      });
      this.setSearchParams(filters);
      this.fetchAllocatedAccomplished();
    },
    countfilters(filter) {
      const val = this.searchParams[filter].filter(
        (param) => param.selected
      ).length;
      if (val == 0) return '';
      return ` (${val})`;
    },
  },

  setup() {
    const { brands } = Auth.useState(['brands']);
    const { salesChannel } = Auth.useState(['salesChannel']);
    const { billingUnits } = Auth.useState(['billingUnits']);
    const { sessionUser, getFilteredDateYYYYMM } = Auth.useGetters([
      'sessionUser',
      'getFilteredDateYYYYMM',
    ]);

    const { SET_ACCESS_TYPE } = Auth.useMutations(['SET_ACCESS_TYPE']);

    const { sessionUpdate } = Auth.useActions(['sessionUpdate']);

    const route = useRoute();

    onBeforeMount(() => SET_ACCESS_TYPE('panel'));

    const state: State = reactive<State>({
      brandFilters: [],
      salesChannelFilters: [],
      billingUnitsEmp: [],
      billingUnitsFil: [],
      segmentFilters: [],
    });

    brands?.value
      ?.filter((el) => el.brandIsPrivateLabel === 'N')
      .forEach((el, index) => {
        state.brandFilters.push({
          id: index,
          fullname: firstUpperString(el.name),
          selected: false,
          code: el.code,
        });
      });

    salesChannel?.value?.forEach((el, index) => {
      state.salesChannelFilters.push({
        id: index,
        fullname:
          el.name == 'export' ? 'Exportação' : firstUpperString(el.name),
        selected: false,
        code: el.code,
      });
    });

    billingUnits?.value?.forEach((el) => {
      const emp = {
        id: state.billingUnitsEmp.length,
        fullname: String(el.businessCode),
        selected: false,
        code: el.businessCode,
      };

      const fil = {
        id: state.billingUnitsFil.length,
        fullname: String(el.subsidiaryCode),
        selected: false,
        code: el.subsidiaryCode,
      };

      if (!state.billingUnitsEmp.some((e) => e.code === emp.code)) {
        state.billingUnitsEmp.push(emp);
      }
      if (!state.billingUnitsFil.some((e) => e.code === fil.code)) {
        state.billingUnitsFil.push(fil);
      }
    });

    state.segmentFilters = [
      {
        id: 0,
        fullname: 'Calçado',
        selected: false,
        code: 1,
      },
      {
        id: 1,
        fullname: 'Acessório',
        selected: false,
        code: 2,
      },
      {
        id: 2,
        fullname: 'Confecção',
        selected: false,
        code: 5,
      },
    ];

    return {
      ...toRefs(state),
      SET_ACCESS_TYPE,
      sessionUpdate,
      sessionUser,
      route,
      getFilteredDateYYYYMM,
    };
  },

  beforeMount() {
    this.setInitialSearchParams();
    this.applySearchParams();
  },
});
